import { ReloadOutlined } from '@ant-design/icons';
import type { BudgetUsageRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayTime } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Input, Select, Space, Switch, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';
import { hq } from '../api/client';
import { sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

/** 预算执行：使用流水（只读），超预算行高亮。 */
export default function BudgetUsage() {
  const currentYear = dayjs().year();
  const [year, setYear] = useState(String(currentYear));
  const [deptId, setDeptId] = useState('');
  const [overOnly, setOverOnly] = useState(false);

  const depts = useQuery({
    queryKey: ['budget', 'dept-options'],
    queryFn: () => hq.orgStructure.dept.list({}),
    staleTime: 10 * 60 * 1000
  });

  const query = useInfiniteQuery({
    queryKey: ['budget', 'usage', year, deptId, overOnly],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.billing.budget.usage({
        lastId: pageParam,
        size: PAGE_SIZE,
        budgetYear: Number(year) || undefined,
        deptId: deptId || undefined,
        overBudgetOnly: overOnly
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<BudgetUsageRow> = [
    { title: '部门', dataIndex: 'deptName', width: 160, render: (value: string | null) => value || '-' },
    { title: '年度', dataIndex: 'budgetYear', width: 90, align: 'right' },
    { title: '期间', dataIndex: 'periodNo', width: 90, align: 'right' },
    { title: '业务类型', dataIndex: 'bizType', width: 130, render: (value: string | null) => value || '-' },
    { title: '业务单号', dataIndex: 'bizNo', width: 200, render: (value: string | null) => value || '-' },
    { title: '使用类型', dataIndex: 'usageType', width: 120, render: (value: string | null) => value || '-' },
    { title: '本次金额', dataIndex: 'amount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    { title: '使用前', dataIndex: 'beforeUsed', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    { title: '使用后', dataIndex: 'afterUsed', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    {
      title: '执行率',
      dataIndex: 'execRateSnapshot',
      width: 110,
      align: 'right',
      render: (value: number) => `${(value * 100).toFixed(1)}%`
    },
    {
      title: '超预算',
      dataIndex: 'overBudgetFlag',
      width: 100,
      render: (value: boolean) => (value ? <Tag color="red">超预算</Tag> : <Tag color="green">正常</Tag>)
    },
    { title: '业务时间', dataIndex: 'bizDate', width: 160, render: (value: string | null) => formatDisplayTime(value) }
  ];

  return (
    <Card
      className="page-card"
      title="预算执行"
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} loading={query.isFetching}>
          刷新
        </Button>
      }
    >
      <Space wrap style={{ marginBottom: 12 }}>
        <Input value={year} onChange={(event) => setYear(event.target.value)} placeholder="年度" style={{ width: 110 }} />
        {/* 部门改选择器：手填 dept_id 既猜不到内部 ID，写错只会查出空列表 */}
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="部门"
          value={deptId || undefined}
          onChange={(value?: string) => setDeptId(value ?? '')}
          loading={depts.isPending}
          options={(depts.data ?? []).map((row) => ({ value: row.id, label: row.deptName }))}
          style={{ width: 220 }}
        />
        <Space size={6}>
          <Switch size="small" checked={overOnly} onChange={setOverOnly} />
          <Typography.Text type="secondary">只看超预算</Typography.Text>
        </Space>
      </Space>

      {query.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="预算流水加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}

      <Table<BudgetUsageRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: query.isError ? '加载失败，请重试' : '暂无预算流水' }}
        rowClassName={(row) => (row.overBudgetFlag ? 'budget-over-row' : '')}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {query.hasNextPage ? (
          <Button onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>
    </Card>
  );
}
